/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { Form, Input, notification, Radio } from 'antd';
import { Button as CrceButton } from '@crc-evans/ui-components';
import { X } from 'lucide-react';
import './ShipmentDashBoardPage.css';
import { OnAddorUpdateShipmentNumber } from '../../redux/ShipmentDetails/saga';
import {
  SHIPMENT_PRIORITY_OPTIONS,
  SHIPMENT_CATEGORY_OPTIONS,
  SHIPMENT_PACKAGING_OPTIONS,
  normalizeShipmentMetaFromRecord,
  packagingFormValueToApi,
} from './shipmentFieldOptions';
import CrceSelect from './CrceSelect';

const AddNewShipmentNumber = (props) => {
  const {
    showAddModel,
    setShowAddModel,
    selectedData,
    editRecord,
    setIsEdit,
    isEdit,
    OnfetchData,
  } = props;

  const [shipmentNumber, setShipmentNumber] = useState('');
  const [priority, setPriority] = useState('medium');
  const [shipmentCategory, setShipmentCategory] = useState('project');
  const [packagingSubcategory, setPackagingSubcategory] = useState(undefined);
  const [addLoading, setAddLoading] = useState(false);
  const [equipTypeForm] = Form.useForm();

  useEffect(() => {
    if (!showAddModel) return;
    if (isEdit && editRecord) {
      const num =
        editRecord.shipmentNumber ??
        editRecord.shipment_number ??
        '';
      setShipmentNumber(String(num));
      const meta = normalizeShipmentMetaFromRecord(editRecord);
      setPriority(meta.priority);
      setShipmentCategory(meta.shipmentCategory);
      setPackagingSubcategory(meta.packagingSubcategory);
    } else if (!isEdit) {
      setShipmentNumber('');
      const meta = normalizeShipmentMetaFromRecord(null);
      setPriority(meta.priority);
      setShipmentCategory(meta.shipmentCategory);
      setPackagingSubcategory(undefined);
    }
  }, [showAddModel, isEdit, editRecord]);

  const handleOk = async () => {
    const num = String(shipmentNumber || '').trim();
    if (!num) {
      notification.warning({
        message: 'Please enter a shipment number to proceed!',
      });
      return;
    }
    if (!packagingSubcategory) {
      notification.warning({
        message: 'Please select packaging (subcategory) to proceed!',
      });
      return;
    }

    const shipmentPayload = {
      shipmentTypeId: selectedData.id,
      shipmentNumber: num,
      priority,
      shipmentCategory,
      packagingType: packagingFormValueToApi(packagingSubcategory),
    };

    let obj = { shipment: shipmentPayload };
    if (isEdit) {
      obj = {
        id: editRecord.id,
        data: { shipment: shipmentPayload },
      };
    }
    setAddLoading(true);
    const res = await OnAddorUpdateShipmentNumber(obj, isEdit);
    if (res) await OnfetchData();
    setAddLoading(false);

    resetLocal();
    setIsEdit(false);
    setShowAddModel(false);
  };

  const resetLocal = () => {
    setShipmentNumber('');
    setPriority('medium');
    setShipmentCategory('project');
    setPackagingSubcategory(undefined);
  };

  const handleCancel = () => {
    setShowAddModel(false);
    resetLocal();
    setIsEdit(false);
  };

  if (!showAddModel) return null;

  return (
    <div className="mc-dpr-modal-backdrop" role="presentation">
      <div
        className="mc-dpr-modal mc-dpr-modal--wide"
        role="dialog"
        aria-labelledby="ship-num-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mc-dpr-modal-head">
          <h3 id="ship-num-modal-title" className="mc-dpr-modal-title">
            {isEdit ? 'Edit shipment number' : 'Add shipment number'}
          </h3>
          <button type="button" className="mc-dpr-modal-close" aria-label="Close" onClick={handleCancel}>
            <X size={20} />
          </button>
        </div>
        <div className="mc-dpr-modal-body">
          <Form
            form={equipTypeForm}
            name="shipment-number-form"
            layout="vertical"
            autoComplete="off"
            colon={false}
          >
            <div className="mc-dpr-modal-form-grid">
              <div className="mc-dpr-modal-form-row">
                <div className="mc-dpr-modal-field">
                  <label htmlFor="ship-num-priority">
                    Priority <span className="required">*</span>
                  </label>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <CrceSelect
                      id="ship-num-priority"
                      size="large"
                      placeholder="Select priority"
                      allowClear
                      value={priority || undefined}
                      onChange={setPriority}
                      options={SHIPMENT_PRIORITY_OPTIONS.map((o) => ({
                        label: o.label,
                        value: o.value,
                      }))}
                    />
                  </Form.Item>
                </div>
              </div>
              <div className="mc-dpr-modal-form-row">
                <div className="mc-dpr-modal-field">
                  <label htmlFor="ship-num-packaging">
                    Subcategory (packaging) <span className="required">*</span>
                  </label>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <CrceSelect
                      id="ship-num-packaging"
                      size="large"
                      placeholder="Select packaging type"
                      allowClear
                      value={packagingSubcategory || undefined}
                      onChange={setPackagingSubcategory}
                      options={SHIPMENT_PACKAGING_OPTIONS.map((o) => ({
                        label: o.label,
                        value: o.value,
                      }))}
                    />
                  </Form.Item>
                </div>
              </div>
              <div className="mc-dpr-modal-form-row mc-dpr-modal-form-row--span-2">
                <div className="mc-dpr-modal-field">
                  <span className="mc-dpr-modal-field-label">
                    Category <span className="required">*</span>
                  </span>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Radio.Group
                      className="shipment-category-radio-group"
                      value={shipmentCategory}
                      onChange={(e) => setShipmentCategory(e.target.value)}
                    >
                      {SHIPMENT_CATEGORY_OPTIONS.map((o) => (
                        <Radio key={o.value} value={o.value}>
                          {o.label}
                        </Radio>
                      ))}
                    </Radio.Group>
                  </Form.Item>
                </div>
              </div>
              <div className="mc-dpr-modal-form-row mc-dpr-modal-form-row--span-2">
                <div className="mc-dpr-modal-field">
                  <label htmlFor="ship-num-number">
                    Shipment number <span className="required">*</span>
                  </label>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Input
                      id="ship-num-number"
                      placeholder="Enter shipment number"
                      value={shipmentNumber}
                      onChange={(e) => setShipmentNumber(e.target.value)}
                    />
                  </Form.Item>
                </div>
              </div>
            </div>
          </Form>
        </div>
        <div className="mc-dpr-modal-foot">
          <CrceButton label="Cancel" variant="tertiary" size="medium" onClick={handleCancel} />
          <CrceButton
            label={addLoading ? (isEdit ? 'Updating…' : 'Adding…') : isEdit ? 'Update' : 'Add'}
            variant="primary"
            size="medium"
            onClick={handleOk}
            disabled={addLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default AddNewShipmentNumber;
